import { apiErrorText } from "../../api/api-error.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * The words and the small pure rules of New campaign and Campaign (P14-02).
 *
 * Kept apart from the two screens so each rule is testable without mounting anything, and so
 * the two screens cannot word the same fact two ways. Copy, never configuration: nothing here
 * has a value a deployment could get wrong.
 *
 * AMENDED 2026-10-09 (plan-015 CMP, G16, G17): the end date's skip (`campaign_ended`) in People not called, the
 * campaign's start and end in Settings in use ({@link campaignRunText}), and the edit-before-start refusal.
 */
import { ApiError } from "@wetalk/api-client";
import type { SegmentedOption, TagStatus, TagTone } from "@wetalk/ui";

import { UndocumentedOperationError } from "../../api/gateway.js";
import { skipReasonLabel, type VoicemailDetectionModeView } from "../../api/outbound-view.js";
import type {
  CallingRangeView,
  CampaignQuestionDraftView,
  CampaignQuestionKindView,
  CampaignSkipReasonView,
  CampaignStateView,
  ReturningPersonTreatmentView,
} from "../../api/gateway.js";
import type {
  CampaignDialRefusalView,
  CampaignDiallingStateView,
  CampaignEndedSkipView,
  CampaignSipTrunkRouteView,
  CampaignSkipWordView,
} from "../../api/slice/campaign.js";

import { callingHoursProblemText } from "./calling-hours.js";
import { dateOfDmy, dayTextOf, dmyOfIsoDate } from "./new-campaign-plan.js";
import type { SipTrunkView } from "../../api/slice/sip-trunk.js";
import type { VoiceAgentQuestionKindView, VoiceAgentQuestionView } from "../../api/slice/campaign.js";
import { questionTextOf } from "./survey-result-copy.js";
import type { Money, TalkRateKind } from "@wetalk/domain";
import { talkRateKindLabel, talkRateText } from "../../api/slice/talk-rate.js";

/** `1,840`. The same grouping every P14 screen uses. */
export function countText(count: number): string {
  return count.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-IE"));
}

// -------------------------------------------------------------------------------------------
// Campaign state
// -------------------------------------------------------------------------------------------

const STATE_LABEL: Readonly<Record<CampaignStateView, string>> = {
  get draft() { return portalText("operations", "draft_23d33e22"); },
  get scheduled() { return portalText("operations", "scheduled_1cd1bdad"); },
  get running() { return portalText("operations", "running_73989d9c"); },
  get paused() { return portalText("operations", "paused_c7dfb6f1"); },
  get finished() { return portalText("operations", "finished_355bcc57"); },
  // 2026-10-04: `cancelled` is a campaign a member ended (`endCampaign`); the owner's word is "Ended".
  get cancelled() { return portalText("operations", "ended_90303d8d"); },
};

export function campaignStateLabel(state: CampaignStateView): string {
  return STATE_LABEL[state];
}

/** The design draws Running in the accent and every other state in neutral. */
export function campaignStateTone(state: CampaignStateView): TagStatus {
  switch (state) {
    case "running":
      return "positive";
    case "scheduled":
    case "paused":
      return "caution";
    default:
      return "muted";
  }
}

/** `setCampaignPaused` accepts `scheduled`/`running` → paused and `paused` → resumed. Nothing else. */
export function pauseActionOf(state: CampaignStateView): "pause" | "resume" | null {
  if (state === "scheduled" || state === "running") return "pause";
  if (state === "paused") return "resume";
  return null;
}

/**
 * The one way to stop a campaign for good (2026-10-04): Delete while it has called nobody, End once it has
 * — never both. `null` for a campaign already over (finished or ended), which can only be read.
 */
export function stopActionOf(campaign: { readonly state: CampaignStateView; readonly deletable: boolean }): "end" | "delete" | null {
  if (campaign.deletable) return "delete";
  if (campaign.state === "scheduled" || campaign.state === "running" || campaign.state === "paused") return "end";
  return null;
}

/** What each confirmation says, before anything is changed. */
export const END_CONFIRMATION =
  () => (portalText("operations", "nobody_more_is_called_for_good_an_ended_campaign_cannot_be_af0d39a2"));
export const DELETE_CONFIRMATION =
  () => (portalText("operations", "nobody_has_been_called_yet_so_nothing_is_lost_the_campaign_8e1f70a8"));

// -------------------------------------------------------------------------------------------
// Dialling through the account's own SIP trunk (plan-008 ST-UA, design §22.14)
// -------------------------------------------------------------------------------------------

/**
 * The P14 sentence every campaign surface says beside the caller-ID number. plan-031 VMD-WAPP (design §42.11): it
 * follows the voicemail detection switch the API reports — "detected and hung up at once; the seconds until then are
 * billed" only while it reads `on`, today's words for `shadow`, `off`, or while no read has said (`null`), so it
 * never claims a detection that is off. No price in either.
 */
export const VOICEMAIL_SENTENCE = (mode: VoicemailDetectionModeView | null): string =>
  mode === "on"
    ? portalText("operations", "voicemail.sentence_on")
    : portalText("operations", "if_a_voicemail_answers_that_conversation_is_billed_talk_ti_9228e1b1");

/**
 * When a campaign tries a person again (design §42.10; the worker's settlement): after no answer, a busy line or a
 * rejected call (plan-033 OD-23, 2026-10-28: a busy leg settles `no_answer` and is retried), and — only while detection
 * is `on` — after a voicemail. `null` until a read has said which, so nothing is claimed in the meantime.
 */
export function retryRuleSentenceOf(mode: VoicemailDetectionModeView | null): string | null {
  if (mode === null) return null;
  return mode === "on" ? portalText("operations", "voicemail.retry_on") : portalText("operations", "voicemail.retry_off");
}

/**
 * The outcome bars the Campaign screen draws (plan-031 VMD-WAPP): every outcome the API answers, except a Voicemail
 * bar at nought while detection is not `on` — a bar for something that cannot happen would read as a claim that it
 * can. One with people in it (detection was on when they were called) is always drawn.
 *
 * plan-033 OD-23 (2026-10-28): the same for `declined` ("Busy or rejected, not called again"), always — the dialler no
 * longer writes it (a busy leg settles `no_answer` and is retried), so a bar is drawn only for a campaign whose people
 * were settled `declined` before the change.
 */
export function shownCampaignOutcomeOf<Outcome extends { readonly code: string; readonly count: number }>(
  outcome: readonly Outcome[],
  mode: VoicemailDetectionModeView,
): readonly Outcome[] {
  return outcome.filter(
    (one) => one.count > 0 || (one.code !== "declined" && (one.code !== "voicemail" || mode === "on")),
  );
}

const DIALLING_LABEL: Readonly<Record<CampaignDiallingStateView, string>> = {
  get dialling() { return portalText("operations", "dialling_299c9f9c"); },
  get paused() { return portalText("operations", "paused_c7dfb6f1"); },
  get outside_window() { return portalText("operations", "not_started_db2c45d5"); },
  get no_route() { return portalText("operations", "no_trunk_route_7f0cc92d"); },
  get switched_off() { return portalText("operations", "switched_off_a18609ea"); },
  get finished() { return portalText("operations", "finished_355bcc57"); },
};

const DIALLING_SENTENCE: Readonly<Record<CampaignDiallingStateView, string>> = {
  get dialling() { return portalText("operations", "people_are_being_rung_each_inside_the_calling_hours_in_the_6db19844"); },
  get paused() { return portalText("operations", "nobody_is_rung_until_you_resume_it_6217250f"); },
  get outside_window() { return portalText("operations", "nobody_is_rung_before_its_start_a0810750"); },
  get no_route() { return portalText("operations", "nobody_is_rung_the_campaign_has_no_route_through_your_sip_8f321ef0"); },
  get switched_off() { return portalText("operations", "nobody_is_rung_campaign_dialling_is_switched_off_for_this_f62e2f00"); },
  get finished() { return portalText("operations", "nobody_more_is_rung_d86189f2"); },
};

/** The dialling state as a short tag: `No trunk route`, `Switched off`, … */
export function diallingStateLabel(state: CampaignDiallingStateView): string {
  return DIALLING_LABEL[state];
}

/** Whether anybody is being rung, and why not, in one sentence. */
export function diallingStateSentence(state: CampaignDiallingStateView): string {
  return DIALLING_SENTENCE[state];
}

export function diallingStateTone(state: CampaignDiallingStateView): TagStatus {
  switch (state) {
    case "dialling":
      return "positive";
    case "no_route":
    case "switched_off":
    case "paused":
      return "caution";
    default:
      return "muted";
  }
}

/** What the campaign's caller-ID number is worth as a route, and what to do when it is not one. */
export function sipTrunkRouteSentence(route: CampaignSipTrunkRouteView, callerIdE164: string | null): string {
  const number = callerIdE164 ?? portalText("operations", "its_caller_id_number_53f904b4");
  switch (route) {
    case "ready":
      return portalText("operations", "calls_from_value_through_your_sip_trunk_d00be40b", { v0: String(number) });
    case "no_caller_id":
      return portalText("operations", "it_has_no_caller_id_number_to_call_from_campaigns_created_28a1f657");
    case "caller_id_not_bound":
      return (
        portalText("operations", "value_is_not_a_caller_id_number_bound_to_this_agent_on_you_2684e228", { v0: String(number) })
      );
    case "sip_trunk_unverified":
      return portalText("operations", "its_sip_trunk_is_not_verified_yet_check_the_connection_or_4fbb4178");
    case "sip_trunk_disabled":
      return portalText("operations", "its_sip_trunk_is_disabled_enable_it_on_numbers_sip_03da3165");
  }
}

/** One number a campaign (or "Ring a number now") may call from. */
export interface CallerIdChoice {
  readonly phoneNumberId: string;
  readonly e164: string;
  readonly sipTrunkDisplayName: string;
}

/**
 * The numbers this agent may call from: caller-ID numbers bound to it on a verified trunk — the same
 * rule the API applies (`caller_id_not_bound`, `sip_trunk_unverified`, `sip_trunk_disabled`).
 */
export function callerIdChoicesOf(trunk: readonly SipTrunkView[], voiceAgentId: string): CallerIdChoice[] {
  return trunk
    .filter((one) => one.state === "verified")
    .flatMap((one) =>
      one.number
        .filter((number) => number.isCallerId && number.voiceAgentId === voiceAgentId)
        .map((number) => ({
          phoneNumberId: number.phoneNumberId,
          e164: number.e164,
          sipTrunkDisplayName: one.displayName,
        })),
    );
}

/**
 * Why this agent has no number to call from, naming the first thing to fix — `null` when it has one.
 * Read in the order the member would fix it: a trunk, then a caller-ID number bound to this agent,
 * then the trunk's verification.
 */
export function callerIdGapOf(trunk: readonly SipTrunkView[], voiceAgentId: string): string | null {
  if (callerIdChoicesOf(trunk, voiceAgentId).length > 0) return null;
  if (trunk.length === 0) {
    return portalText("operations", "outbound_calls_go_through_your_own_sip_trunk_and_this_acco_3bb47e3b");
  }
  const bound = trunk.filter((one) =>
    one.number.some((number) => number.isCallerId && number.voiceAgentId === voiceAgentId),
  );
  if (bound.length === 0) {
    return (
      portalText("operations", "none_of_your_sip_trunk_s_numbers_is_a_caller_id_number_con_98b84853")
    );
  }
  if (bound.every((one) => one.state === "disabled")) {
    return portalText("operations", "the_sip_trunk_of_this_agent_s_caller_id_number_is_disabled_be8bacb2");
  }
  return portalText("operations", "the_sip_trunk_of_this_agent_s_caller_id_number_is_not_veri_b9fd0a36");
}

// -------------------------------------------------------------------------------------------
// Questions
// -------------------------------------------------------------------------------------------

/** The design's tag per kind: Scale in the accent, Choice neutral, Open outlined. */
export function questionKindTone(kind: CampaignQuestionKindView): TagTone {
  switch (kind) {
    case "scale":
      return "accent";
    case "choice":
      return "neutral";
    case "open":
      return "outline";
  }
}

export const QUESTION_KIND_OPTION: readonly { readonly value: CampaignQuestionKindView; readonly label: string }[] = [
  { value: "scale", get label() { return portalText("operations", "scale_one_to_five_e78fbd36"); } },
  { value: "choice", get label() { return portalText("operations", "choice_0682be26"); } },
  { value: "open", get label() { return portalText("operations", "open_cf9b7706"); } },
];

/** One row of the question editor, before it becomes a draft. */
export interface QuestionEntry {
  /** A key for React only; never sent. */
  readonly key: string;
  readonly text: string;
  readonly kind: CampaignQuestionKindView;
}

/**
 * The editor's rows as the API's `QuestionDraft[]`.
 *
 * The code is the key answers are filed under (survey exports and the By person table read
 * it), so it must be distinct within a set and should not collide ACROSS the two sets either:
 * a returning person's answer to their first question must not land in the same column as a
 * first-time person's answer to theirs. Hence `q1…` for the main set and `r1…` for the
 * returning set, both matching `^[a-z][a-z0-9_]{0,63}$`.
 */
export function questionDraftsOf(
  entry: readonly QuestionEntry[],
  set: "primary" | "returning",
): CampaignQuestionDraftView[] {
  const prefix = set === "primary" ? "q" : "r";
  return entry.map((one, index) => ({
    ordinal: index + 1,
    code: `${prefix}${String(index + 1)}`,
    text: one.text.trim(),
    kind: one.kind,
  }));
}

// -------------------------------------------------------------------------------------------
// The agent's own questionnaire (2026-10-04)
// -------------------------------------------------------------------------------------------

/** What New campaign and Campaign say above the agent's questions. */
export const VOICE_AGENT_QUESTION_NOTE =
  () => (portalText("operations", "the_agent_asks_the_questions_it_was_built_with_to_change_t_f979004c"));

/** A statement is answered on the questionnaire's scale; the emotion and source questions by choosing. */
export function voiceAgentQuestionKindLabel(question: Pick<VoiceAgentQuestionView, "questionKind" | "selectionMax">): string {
  switch (question.questionKind) {
    case "statement":
      return portalText("operations", "scale_a29f0256");
    case "emotion":
    case "source":
    case "choice":
      return (question.selectionMax ?? 1) > 1 ? portalText("operations", "choice_up_to_value_a17dc273", { v0: String(String(question.selectionMax)) }) : portalText("operations", "choice_0682be26");
    // ADR 0053: a number question and an open question, anywhere in the questionnaire.
    case "number":
      return portalText("operations", "number_ad53a007");
    case "open":
      return portalText("operations", "open_answer_ad53a008");
  }
}

export function voiceAgentQuestionKindTone(kind: VoiceAgentQuestionKindView): TagTone {
  return kind === "statement" ? "accent" : "neutral";
}

/** "Arm A" beside a statement when the questionnaire has two arms; `null` with one. */
export function voiceAgentQuestionArmText(question: Pick<VoiceAgentQuestionView, "variant">, armCount: number): string | null {
  return armCount > 1 && question.variant !== null ? portalText("operations", "arm_value_15f37569", { v0: String(question.variant) }) : null;
}

/** How many arms (A, B) the questionnaire's statements are split into. */
export function armCountOf(question: readonly Pick<VoiceAgentQuestionView, "variant">[]): number {
  return new Set(question.map((one) => one.variant).filter((one) => one !== null)).size;
}

/** The choices of a choice question, in one line, in the given language (else the questionnaire's first). */
export function voiceAgentQuestionChoiceText(question: Pick<VoiceAgentQuestionView, "option">, languageCode: string | null): string | null {
  if (question.option.length === 0) return null;
  return question.option
    .map((one) => questionTextOf(one.label, languageCode) ?? one.answer)
    .join(" · ");
}

// -------------------------------------------------------------------------------------------
// The New campaign form's choices
// -------------------------------------------------------------------------------------------

export const RETURNING_PERSON_OPTION: readonly SegmentedOption<ReturningPersonTreatmentView>[] = [
  { value: "same_questions", get label() { return portalText("operations", "same_questions_20d76a6c"); } },
  { value: "different_questions", get label() { return portalText("operations", "different_questions_42b8b548"); } },
  { value: "skip_them", get label() { return portalText("operations", "skip_them_0572ee86"); } },
];

/**
 * The choices for people already surveyed. An agent that asks its own questionnaire has one set of
 * questions, so "Different questions" is not offered (the API refuses it, `returning_person_unavailable`).
 */
export function returningPersonOptionsOf(asksQuestionnaire: boolean): readonly SegmentedOption<ReturningPersonTreatmentView>[] {
  return asksQuestionnaire ? RETURNING_PERSON_OPTION.filter((one) => one.value !== "different_questions") : RETURNING_PERSON_OPTION;
}

/** Once, twice, three times — the design's three, as `attempts_per_person`. */
export const ATTEMPT_OPTION: readonly SegmentedOption<"1" | "2" | "3">[] = [
  { value: "1", get label() { return portalText("operations", "once_d9c76878"); } },
  { value: "2", get label() { return portalText("operations", "twice_ccfdecde"); } },
  { value: "3", get label() { return portalText("operations", "three_times_8e725a02"); } },
];

/** The design's four waits, in minutes. */
export const WAIT_OPTION: readonly { readonly minute: number; readonly label: string }[] = [
  { minute: 30, get label() { return portalText("operations", "30_minutes_31bbe889"); } },
  { minute: 240, get label() { return portalText("operations", "4_hours_7b47150d"); } },
  { minute: 1_440, get label() { return portalText("operations", "1_day_2d8a6f7c"); } },
  { minute: 4_320, get label() { return portalText("operations", "3_days_09ad9df4"); } },
];

/** ISO weekday numbers, Monday = 1, with the design's labels. */
export const DAY_OPTION: readonly { readonly day: number; readonly label: string }[] = [
  { day: 1, get label() { return portalText("operations", "mon_24b2a099"); } },
  { day: 2, get label() { return portalText("operations", "tue_529541bb"); } },
  { day: 3, get label() { return portalText("operations", "wed_23408b19"); } },
  { day: 4, get label() { return portalText("operations", "thu_3593ccd9"); } },
  { day: 5, get label() { return portalText("operations", "fri_bbd6e32e"); } },
  { day: 6, get label() { return portalText("operations", "sat_6b782d41"); } },
  { day: 7, get label() { return portalText("operations", "sun_48c98cab"); } },
];

export type StartChoice = "as_soon_as_ready" | "on_a_date";

export const START_OPTION: readonly SegmentedOption<StartChoice>[] = [
  { value: "as_soon_as_ready", get label() { return portalText("operations", "as_soon_as_it_is_ready_2a312eda"); } },
  { value: "on_a_date", get label() { return portalText("operations", "on_a_date_62d53b71"); } },
];

const LOCAL_TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * Whether the two times make a window the API accepts: both `HH:MM` and the first earlier.
 * A window across midnight is refused by the API, because the hours are the PERSON's own and
 * nobody is surveyed in the middle of their night.
 */
export function callingWindowProblem(fromLocalTime: string, toLocalTime: string): string | null {
  if (!LOCAL_TIME.test(fromLocalTime) || !LOCAL_TIME.test(toLocalTime)) {
    return portalText("operations", "write_both_times_as_hh_mm_in_24_hour_time_e0f66aee");
  }
  if (fromLocalTime >= toLocalTime) {
    return portalText("operations", "the_first_time_must_be_earlier_than_the_second_a_window_ca_76fcad62");
  }
  return null;
}

/**
 * Where the chosen agent's questions come from (2026-10-04): its own `questionnaire` (nothing to type),
 * `typed` for an agent whose live version carries none (the API then needs a typed list), `unknown`
 * while they are being read, or when they could not be.
 */
export type DraftQuestionSource = "questionnaire" | "typed" | "unknown";

/** Everything the form still needs before it can be started, in the order it appears. */
export interface DraftGap {
  readonly voiceAgentChosen: boolean;
  readonly questionSource: DraftQuestionSource;
  /** plan-008: the number the campaign calls from — the API refuses a draft without one. */
  readonly callerIdChosen: boolean;
  readonly name: string;
  readonly contactListId: string;
  readonly question: readonly QuestionEntry[];
  readonly returningPerson: ReturningPersonTreatmentView;
  readonly returningQuestion: readonly QuestionEntry[];
  /** 2026-10-30: the calling ranges per weekday. */
  readonly callingHours: readonly CallingRangeView[];
  readonly start: StartChoice;
  readonly startsAtLocal: string;
}

export function missingOf(draft: DraftGap): string[] {
  const missing: string[] = [];
  if (!draft.voiceAgentChosen) missing.push(portalText("operations", "choose_which_agent_runs_the_campaign_f4803ce3"));
  if (draft.voiceAgentChosen && !draft.callerIdChosen) missing.push(portalText("operations", "choose_the_number_the_campaign_calls_from_014856d7"));
  if (draft.name.trim() === "") missing.push(portalText("operations", "give_the_campaign_a_name_f4cb469b"));
  if (draft.contactListId === "") missing.push(portalText("operations", "pick_who_to_call_b82cd61a"));
  if (draft.voiceAgentChosen && draft.questionSource === "unknown") {
    missing.push(portalText("operations", "wait_until_the_agent_s_questions_have_been_read_f6e8234b"));
  }
  // An agent that asks its own questionnaire needs nothing typed here (2026-10-04).
  if (draft.questionSource === "typed") missing.push(...typedQuestionGap(draft));
  const hours = callingHoursProblemText(draft.callingHours);
  if (hours !== null) missing.push(hours);
  if (draft.start === "on_a_date" && draft.startsAtLocal === "") missing.push(portalText("operations", "pick_the_date_to_start_4f78ea55"));
  return missing;
}

/** What a typed question list still needs, for an agent with no questionnaire of its own. */
function typedQuestionGap(draft: DraftGap): string[] {
  const missing: string[] = [];
  if (draft.question.length === 0) missing.push(portalText("operations", "add_at_least_one_question_db7d8ecb"));
  draft.question.forEach((one, index) => {
    if (one.text.trim() === "") missing.push(portalText("operations", "question_value_has_no_words_yet_d589b972", { v0: String(String(index + 1)) }));
  });
  if (draft.returningPerson === "different_questions") {
    if (draft.returningQuestion.length === 0) {
      missing.push(portalText("operations", "add_at_least_one_question_for_people_already_surveyed_1df34165"));
    }
    draft.returningQuestion.forEach((one, index) => {
      if (one.text.trim() === "") {
        missing.push(portalText("operations", "returning_question_value_has_no_words_yet_7ceaeee3", { v0: String(String(index + 1)) }));
      }
    });
  }
  return missing;
}

/**
 * The start moment as the API's RFC 3339 instant, read in the member's own browser zone.
 *
 * This is WHEN dialling may begin, which is a moment for the person setting it up. It is not
 * the calling hours: those stay each recipient's own, and the note under the field says so.
 */
export function startsAtOf(start: StartChoice, startsAtLocal: string): string | null {
  if (start === "as_soon_as_ready" || startsAtLocal === "") return null;
  const moment = new Date(startsAtLocal);
  if (Number.isNaN(moment.getTime())) return null;
  return moment.toISOString();
}

// -------------------------------------------------------------------------------------------
// Skipped people (AC-31)
// -------------------------------------------------------------------------------------------

export const SKIP_REASON_SEQUENCE: readonly (CampaignSkipReasonView | CampaignEndedSkipView)[] = [
  "suppressed",
  "already_surveyed",
  "called_within_window",
  "no_answer_limit",
  "outside_local_calling_hours",
  "unknown_time_zone",
  "invalid_number",
  // plan-015 CMP (G16): the person's own end passed while a retry was still to come, or before they were reached.
  "campaign_ended",
];

/** plan-015 CMP: the end date's skip, in the People not called panel's words. */
const CAMPAIGN_ENDED_WORD = () => (portalText("operations", "the_end_date_passed_in_their_time_zone_before_they_were_re_4ef977b0"));

/** The refusals the dialler met, in the words the People not called panel uses (plan-008, IC-73). */
const DIAL_REFUSAL_WORD: Readonly<Record<CampaignDialRefusalView, string>> = {
  get number_not_bound() { return portalText("operations", "the_caller_id_number_was_not_connected_to_this_agent_1ed9dd4a"); },
  get destination_refused() { return portalText("operations", "wetalk_does_not_ring_that_number_through_your_trunk_4625d319"); },
  get sip_trunk_unverified() { return portalText("operations", "your_sip_trunk_was_not_verified_c6adf8d8"); },
  get voice_agent_paused() { return portalText("operations", "the_agent_was_paused_90066e7d"); },
  get voice_agent_unavailable() { return portalText("operations", "the_agent_had_no_live_version_e55bfd55"); },
  get model_resting() { return portalText("operations", "the_language_model_was_resting_4b0dc7a4"); },
  get draining() { return portalText("operations", "wetalk_s_voice_runtime_was_restarting_a3fab80e"); },
  get sip_edge_unavailable() { return portalText("operations", "wetalk_s_sip_edge_was_not_answering_056f5586"); },
  // plan-015 INT-B: the two refusals SIP (G13) and QSET (G19) added, in the same plain words. QSET-OFF (owner answer
  // 40): no campaign pins a version any more, so the second is only ever an older recorded skip — worded without sets.
  get sip_trunk_inbound_only() { return portalText("operations", "your_sip_trunk_only_receives_calls_so_nothing_was_dialled_da33f70e"); },
  get agent_version_not_eligible() { return portalText("operations", "the_agent_s_questions_were_not_ready_for_this_campaign_88ba028f"); },
};

/** Why one person was not rung: AC-31's reasons, the dialler's refusals, else the word as recorded. */
export function skipWordLabel(reason: CampaignSkipWordView | string): string {
  if (reason === "campaign_ended") return CAMPAIGN_ENDED_WORD();
  const refusal = (DIAL_REFUSAL_WORD as Readonly<Record<string, string>>)[reason];
  return refusal ?? skipReasonLabel(reason);
}

// -------------------------------------------------------------------------------------------
// The start and the end (plan-015 CMP, G16, G17)
// -------------------------------------------------------------------------------------------

/** The design's refusal once a campaign has started (`409 campaign_started`, before 2026-10-30). */
export const CAMPAIGN_STARTED_TEXT = () => (portalText("operations", "this_campaign_has_started_so_it_can_t_be_changed_you_can_p_96cdb551"));

/** 2026-10-30: a finished or ended campaign can't be changed (`409 campaign_finished`). */
export const CAMPAIGN_FINISHED_TEXT = () => (portalText("operations", "this_campaign_has_finished_so_it_can_t_be_changed_f879d38c"));


/** `2026-10-12` → "Mon 12 Oct". */
function localDayText(iso: string): string {
  const date = dateOfDmy(dmyOfIsoDate(iso));
  return date === null ? iso : dayTextOf(date);
}

/**
 * Settings in use's "Runs" row: "From Mon 12 Oct at 10:00 to the end of Fri 23 Oct, in each person's local time".
 * `null` when the gateway does not say (the sandbox).
 */
export function campaignRunText(campaign: {
  readonly startLocalDate?: string | null;
  readonly startLocalTime?: string | null;
  readonly endLocalDate?: string | null;
}): string | null {
  if (campaign.startLocalDate === undefined || campaign.endLocalDate === undefined) return null;
  const from =
    campaign.startLocalDate === null || campaign.startLocalTime === null || campaign.startLocalTime === undefined
      ? portalText("operations", "from_as_soon_as_it_could_start_6152103e")
      : portalText("operations", "from_value_at_value_626d8b58", { v0: String(localDayText(campaign.startLocalDate)), v1: String(campaign.startLocalTime) });
  const to = campaign.endLocalDate === null ? portalText("operations", "until_its_last_retry_a233dd2b") : portalText("operations", "to_the_end_of_value_de050da6", { v0: String(localDayText(campaign.endLocalDate)) });
  return portalText("operations", "valuevalue_in_each_person_s_local_time_007b4f9e", { v0: String(from), v1: String(to) });
}

export function skipFilterLabel(reason: CampaignSkipWordView | "all", count: number): string {
  return reason === "all"
    ? portalText("operations", "every_reason_value_b1faf926", { v0: String(countText(count)) })
    : `${skipWordLabel(reason)} · ${countText(count)}`;
}

// -------------------------------------------------------------------------------------------
// Refusals
// -------------------------------------------------------------------------------------------

/**
 * A refusal, in words, for the codes the campaign operations document (design §15.6.5).
 *
 * An error this does not recognise is shown as its own message: a refusal is never replaced
 * by a reassuring sentence, and "not built yet" is never shown as "nothing here".
 */
/**
 * The slider's note (plan-014 E3b; FR12-ENT-09): the limit is the ORGANIZATION's, shared across every workspace and
 * agent, and inbound callers still come first.
 */
export function organizationConcurrencyNote(simultaneousCall: number, ceiling: number): string {
  return simultaneousCall >= ceiling
    ? portalText("operations", "at_your_organization_s_limit_inbound_callers_still_take_pr_3718a29f")
    : portalText("operations", "inbound_callers_take_priority_so_the_campaign_uses_up_to_v_ced30843", { v0: String(String(simultaneousCall)), v1: String(String(ceiling)) });
}

/**
 * How many calls a campaign of one agent may make at once (campaign fix-up 2026-10-28): the organization's
 * concurrency entitlement, the agent's own line limit (its Capacity, else its live version's — ADR 0045) and the
 * workspace's own line limit, and the lowest of them, which the slider cannot pass. `voiceAgentLimit` is `null` when
 * the agent has no live version; `workspaceLimit` is `null` when the workspace has no limit of its own.
 */
export interface CampaignLineBound {
  readonly ceiling: number;
  readonly entitlement: number;
  readonly voiceAgentLimit: number | null;
  readonly workspaceLimit: number | null;
}

export function campaignLineBoundOf(
  entitlement: number,
  voiceAgentLimit: number | null,
  workspaceLimit: number | null,
): CampaignLineBound {
  const bound = [entitlement, voiceAgentLimit, workspaceLimit].filter((one): one is number => one !== null);
  return { entitlement, voiceAgentLimit, workspaceLimit, ceiling: Math.min(...bound) };
}

/**
 * Whose limit holds the campaign back: the lowest figure, a tie naming the organization before the agent and the agent
 * before the workspace — the order the API names its refusals in.
 */
export function campaignLineBinder(bound: CampaignLineBound): "organization" | "voice_agent" | "workspace" {
  if (bound.entitlement === bound.ceiling) return "organization";
  if (bound.voiceAgentLimit === bound.ceiling) return "voice_agent";
  return "workspace";
}

/** Whose limit it is, and the figure: the slider's upper label and the campaign screen's "At once" title. */
export function campaignLineBoundText(bound: CampaignLineBound | null): string {
  if (bound === null) return portalText("operations", "your_organization_s_limit_appears_once_the_agent_is_chosen_77ca4863");
  const binder = campaignLineBinder(bound);
  if (binder === "voice_agent") return portalText("operations", "this_agent_takes_value_at_once_bc2bee40", { v0: String(countText(bound.ceiling)) });
  if (binder === "workspace") return portalText("operations", "this_workspace_takes_value_at_once_e9360a4a", { v0: String(countText(bound.ceiling)) });
  return portalText("operations", "your_organization_allows_value_93cd3c75", { v0: String(countText(bound.ceiling)) });
}

/** The campaign screen's "At once" title: whose limit holds the saved campaign now, and the figure. */
export function campaignLineScheduleTitle(schedule: {
  readonly simultaneousCallCeiling: number;
  readonly entitlementCeiling: number;
  readonly voiceAgentChannelUnitLimit: number | null;
  readonly workspaceChannelUnitLimit: number | null;
}): string {
  const binder = campaignLineBinder(
    campaignLineBoundOf(schedule.entitlementCeiling, schedule.voiceAgentChannelUnitLimit, schedule.workspaceChannelUnitLimit),
  );
  const v0 = String(countText(schedule.simultaneousCallCeiling));
  if (binder === "voice_agent") return portalText("operations", "this_agent_takes_value_at_once_bc2bee40", { v0 });
  if (binder === "workspace") return portalText("operations", "this_workspace_takes_value_at_once_e9360a4a", { v0 });
  return portalText("operations", "your_organization_allows_value_at_once_32fd9cc4", { v0 });
}

/**
 * Under the slider while the agent's or the workspace's limit holds the campaign back: why the slider stops there (and,
 * for the agent's, where to raise it). `null` while the organization's figure is the lowest (nothing more to explain
 * than the label says).
 */
export function campaignLineLimitHint(bound: CampaignLineBound | null): string | null {
  if (bound === null) return null;
  const binder = campaignLineBinder(bound);
  if (binder === "voice_agent") {
    return portalText("operations", "the_agent_answers_at_most_value_conversations_at_once_its_c_1b1ea964", { v0: String(countText(bound.ceiling)) });
  }
  if (binder === "workspace") {
    return portalText("operations", "this_workspace_holds_at_most_value_conversations_at_once_ac_3c179fa5", { v0: String(countText(bound.ceiling)) });
  }
  return null;
}

export function campaignRefusalText(error: Error): string {
  if (error instanceof UndocumentedOperationError) {
    return (
      portalText("operations", "this_isn_t_available_yet_the_console_needs_the_value_opera_f6a346e1", { v0: String(error.gap.wants), v1: String(error.gap.owner) })
    );
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case "agent_version_not_ready":
        return portalText("operations", "this_agent_has_no_live_version_yet_so_it_cannot_run_a_camp_0433ad22");
      case "campaign_simultaneous_call_over_ceiling":
        return portalText("operations", "that_is_more_calls_at_the_same_time_than_your_organization_426a8d9b");
      // Campaign fix-up 2026-10-28 (ADR 0045): above the agent's own line limit.
      case "campaign_simultaneous_call_over_voice_agent_limit":
        return portalText("operations", "that_is_more_calls_at_the_same_time_than_this_agent_takes_l_e08e9701");
      // Campaign fix-up 2026-10-28: above the workspace's own line limit.
      case "campaign_simultaneous_call_over_workspace_limit":
        return portalText("operations", "that_is_more_calls_at_the_same_time_than_this_workspace_tak_a3f68983");
      case "campaign_calling_window_invalid":
        return portalText("operations", "the_calling_days_or_hours_were_refused_value_b5f9d101", { v0: String(apiErrorText(error, "")) });
      case "contact_list_not_found":
        return portalText("operations", "that_contact_list_no_longer_exists_pick_another_one_910b77df");
      case "voice_agent_not_found":
        return portalText("operations", "that_agent_no_longer_exists_in_this_workspace_b9985b68");
      case "campaign_not_found":
        return portalText("operations", "this_campaign_no_longer_exists_in_this_workspace_9283a247");
      case "campaign_state_conflict":
        return portalText("operations", "this_campaign_has_finished_or_been_ended_so_it_can_no_long_150dc47e");
      // 2026-10-04: the agent's own questionnaire, and Delete.
      case "campaign_question_not_accepted":
        return portalText("operations", "this_agent_asks_the_questions_it_was_built_with_so_the_cam_0a4357f5");
      case "returning_person_unavailable":
        return portalText("operations", "this_agent_asks_the_questions_it_was_built_with_so_people_5cebb576");
      // plan-015 CMP (G17): an edit after the start.
      case "campaign_started":
        return CAMPAIGN_STARTED_TEXT();
      // 2026-10-30: an edit after the start keeps the list and the start; a finished campaign keeps everything.
      case "campaign_field_locked":
        return portalText("operations", "this_campaign_has_started_so_its_contact_list_and_its_star_97ed0ff2");
      case "campaign_finished":
        return CAMPAIGN_FINISHED_TEXT();
      case "campaign_not_deletable":
        return portalText("operations", "this_campaign_has_already_called_somebody_so_it_cannot_be_d16689f6");
      case "permission_denied":
        return portalText("operations", "your_sign_in_is_not_allowed_to_do_that_here_so_nothing_has_6882d6fe");
      case "validation_failed":
        return portalText("operations", "part_of_the_campaign_was_refused_value_1263bd98", { v0: String(apiErrorText(error, "")) });
      // plan-008: the caller-ID number's route through the account's own SIP trunk (ST-AN).
      case "caller_id_not_bound":
        return (
          portalText("operations", "no_route_through_your_sip_trunk_the_number_to_call_from_mu_47d3150b")
        );
      case "sip_trunk_unverified":
        return portalText("operations", "the_caller_id_number_s_sip_trunk_is_not_verified_yet_check_556d829d");
      case "sip_trunk_disabled":
        return portalText("operations", "the_caller_id_number_s_sip_trunk_is_disabled_enable_it_on_1bbcd875");
      default:
        return apiErrorText(error, "");
    }
  }
  return apiErrorText(error, "");
}

/**
 * plan-017 (Q5, OQ-1): the rate a campaign is priced at, named — "Calls out over your own SIP trunk · <rate> a minute".
 * The api says which outbound kind the route dials on and its rate; the words are the glossary's customer copy.
 */
export function outboundRateTextOf(kind: TalkRateKind, rate: Money): string {
  return portalText("operations", "campaign.priced_at", { v0: talkRateKindLabel(kind), v1: talkRateText(rate, "minute") });
}

/** "2 conversations have no price and are left out" under a campaign's spend, or `null` when none was. */
export function campaignPriceUnavailableTextOf(count: number): string | null {
  return count === 0 ? null : portalText("operations", "history.price_unavailable_left_out", { count });
}
